class SectionScrollEffect {
    constructor() {
        // Reuse the controller if initialized again so wheel input and animation
        // are handled only once.
        if (window.imageThemeSectionScrollEffect) {
            return window.imageThemeSectionScrollEffect;
        }

        window.imageThemeSectionScrollEffect = this;
        this.body = document.body;
        this.html = document.documentElement;
        this.enabledClass = "image-theme-scroll-effect-enabled";
        this.contentClass = "image-theme-scroll-effect__content";
        this.sectionClass = "image-theme-scroll-effect__section";
        this.activeClass = "image-theme-scroll-effect__section--active";
        this.tallSectionClass = "image-theme-scroll-effect__section--tall";
        this.constrainInnerClass = "image-theme-scroll-effect__section--constrain-inner";
        this.defaultBackgroundClass = "image-theme-scroll-effect__section--default-background";
        this.defaultBackgroundColor = "#fff";
        this.readyClass = "image-theme-scroll-effect-ready";
        this.contents = [];
        this.sections = [];
        this.sectionTops = new WeakMap();
        this.ticking = false;
        this.shouldForceInitialTop = false;
        this.wheelDelta = 0;
        this.wheelDirection = 0;
        this.wheelResetTimer = null;
        // Lower values move between sections with less wheel input.
        this.wheelThreshold = 25;
        this.isNavigating = false;
        this.isNativeSectionNavigation = false;
        this.navigationFrame = null;
        this.previousScrollBehavior = "";
        this.previousScrollSnapType = "";
        this.isScrollSnapSuspended = false;
        this.suspendedScrollBehavior = "";
        this.suspendedScrollSnapType = "";
        this.tallBoundaryLock = null;
        this.tallBoundaryLockTimer = null;
        // Keep trackpad momentum from skipping a section without making the
        // next intentional wheel gesture feel delayed.
        this.tallBoundaryLockDuration = 100;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchStartScrollTop = 0;
        this.touchStartSectionIndex = -1;
        this.touchStartedAt = 0;
        this.touchTarget = null;
        this.touchSnapTimer = null;
        this.isTouching = false;
        this.touchSnapThreshold = 36;

        this.init();
    }

    init() {
        if (!this.body || !this.body.classList.contains(this.enabledClass)) {
            return;
        }

        this.prepareInitialScrollPosition();
        this.scanSections();

        if (!this.sections.length) {
            return;
        }

        window.addEventListener("scroll", () => {
            this.requestUpdate();
            this.scheduleTouchSectionSnap();
        }, { passive: true });
        window.addEventListener("resize", () => {
            this.cancelSectionNavigation();
            this.scanSections();
            this.requestUpdate();
        });
        window.addEventListener("wheel", (event) => this.handleWheel(event), { passive: false });
        window.addEventListener("touchstart", (event) => this.handleTouchStart(event), { passive: true });
        window.addEventListener("touchend", (event) => this.handleTouchEnd(event), { passive: true });
        window.addEventListener("touchcancel", () => this.resetTouchState(), { passive: true });
        window.addEventListener("load", () => {
            this.scanSections();
            this.requestUpdate();
        });

        this.observeContentChanges();
        this.requestUpdate();
    }

    prepareInitialScrollPosition() {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }

        if (window.location.hash) {
            return;
        }

        this.shouldForceInitialTop = true;

        const forceTop = () => {
            if (!this.shouldForceInitialTop) {
                return;
            }

            window.scrollTo(0, 0);
            this.updateSectionProgress(false);
        };
        const stopForcingTop = () => {
            this.shouldForceInitialTop = false;
        };

        forceTop();

        document.addEventListener("DOMContentLoaded", forceTop, { once: true });
        window.addEventListener("pageshow", forceTop, { once: true });
        window.addEventListener("load", () => {
            forceTop();

            window.requestAnimationFrame(() => {
                forceTop();
                window.requestAnimationFrame(forceTop);
            });

            window.setTimeout(forceTop, 60);
            window.setTimeout(() => {
                forceTop();
                stopForcingTop();
            }, 300);
        }, { once: true });
        window.addEventListener("wheel", stopForcingTop, { once: true, passive: true });
        window.addEventListener("touchstart", stopForcingTop, { once: true, passive: true });
        window.addEventListener("keydown", stopForcingTop, { once: true });
    }

    handleWheel(event) {
        // Read deltaMode before deltaY. Firefox can change the reported delta
        // units based on that access order, so normalizing first keeps the
        // sensitivity independent of the element underneath the pointer.
        const delta = this.getNormalizedWheelDelta(event);

        if (!this.canUseSectionWheelNavigation(event, delta)) {
            return;
        }

        if (this.isNavigating) {
            event.preventDefault();
            return;
        }

        if (!delta) {
            return;
        }

        const direction = delta > 0 ? 1 : -1;
        const currentIndex = this.getCurrentSectionIndex();
        const shouldUseBoundaryLock = event.deltaMode === WheelEvent.DOM_DELTA_PIXEL;

        if (shouldUseBoundaryLock && this.shouldHoldTallBoundary(currentIndex, direction)) {
            event.preventDefault();
            return;
        }

        if (this.scrollTallSectionWithinBounds(currentIndex, delta, direction, shouldUseBoundaryLock)) {
            event.preventDefault();
            return;
        }

        if (currentIndex === -1 || !this.shouldCaptureWheelForSection(currentIndex, direction)) {
            return;
        }

        const targetIndex = currentIndex + direction;

        // Let the browser continue into the normal footer flow after the final content
        // section (and back above the first one), rather than consuming the wheel input.
        if (targetIndex < 0 || targetIndex >= this.sections.length) {
            this.resetWheelIntent();
            return;
        }

        event.preventDefault();

        if (this.wheelDirection && this.wheelDirection !== direction) {
            this.resetWheelIntent();
        }

        this.wheelDirection = direction;
        this.wheelDelta += Math.abs(delta);

        window.clearTimeout(this.wheelResetTimer);
        this.wheelResetTimer = window.setTimeout(() => this.resetWheelIntent(), 350);

        if (this.wheelDelta < this.wheelThreshold) {
            return;
        }

        this.resetWheelIntent();
        this.navigateToSection(targetIndex, { preferNativeSmooth: true, direction });
    }

    canUseSectionWheelNavigation(event, normalizedDeltaY) {
        if (!this.sections.length || event.ctrlKey || event.metaKey || event.shiftKey) {
            return false;
        }

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return false;
        }

        const target = event.target instanceof Element ? event.target : null;

        // Text fields and textareas cover most of the contact section. They do
        // not need exclusive wheel handling unless they actually have internal
        // overflow (handled below), so allow the page to navigate over them.
        // Keep controls whose value can change with the wheel protected.
        if (!target || target.closest("select, button, input[type='number'], input[type='range'], [contenteditable='true'], [data-section-scroll-native]")) {
            return false;
        }

        return !this.hasScrollableParent(target, normalizedDeltaY);
    }

    hasScrollableParent(element, deltaY) {
        let parent = element;

        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const canScrollVertically = /(auto|scroll|overlay)/.test(style.overflowY)
                && parent.scrollHeight > parent.clientHeight;

            if (canScrollVertically) {
                const canScrollDown = deltaY > 0 && parent.scrollTop + parent.clientHeight < parent.scrollHeight - 1;
                const canScrollUp = deltaY < 0 && parent.scrollTop > 1;

                if (canScrollDown || canScrollUp) {
                    return true;
                }
            }

            parent = parent.parentElement;
        }

        return false;
    }

    getNormalizedWheelDelta(event) {
        const deltaMode = event.deltaMode;
        const deltaY = event.deltaY;

        if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
            // Firefox reports wheel notches as lines, so convert them to a
            // pixel-like value before applying the shared sensitivity threshold.
            return deltaY * 30;
        }

        if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
            return deltaY * window.innerHeight;
        }

        return deltaY;
    }

    handleTouchStart(event) {
        if (!this.canUseTouchSectionNavigation(event)) {
            this.resetTouchState();
            return;
        }

        const touch = event.touches && event.touches[0];

        if (!touch) {
            this.resetTouchState();
            return;
        }

        window.clearTimeout(this.touchSnapTimer);
        this.cancelSectionNavigation();
        this.isTouching = true;
        this.touchTarget = event.target instanceof Element ? event.target : null;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        this.touchStartSectionIndex = this.getCurrentSectionIndex();
        this.touchStartedAt = window.performance.now();
    }

    handleTouchEnd(event) {
        if (!this.isTouching || !this.isTouchSectionSnapEnabled()) {
            this.resetTouchState();
            return;
        }

        const touch = event.changedTouches && event.changedTouches[0];
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const scrollDelta = currentScrollTop - this.touchStartScrollTop;

        this.isTouching = false;

        if (touch) {
            const xDelta = touch.clientX - this.touchStartX;
            const yDelta = touch.clientY - this.touchStartY;

            if (Math.abs(xDelta) > Math.abs(yDelta) * 1.2) {
                this.resetTouchState();
                return;
            }
        }

        if (Math.abs(scrollDelta) < 4) {
            this.resetTouchState();
            return;
        }

        this.scheduleTouchSectionSnap(90);
    }

    canUseTouchSectionNavigation(event) {
        if (!this.isTouchSectionSnapEnabled()) {
            return false;
        }

        const target = event.target instanceof Element ? event.target : null;

        if (!target || target.closest("input, textarea, select, button, [contenteditable='true'], [data-section-scroll-native]")) {
            return false;
        }

        return !this.hasScrollableParentInAnyDirection(target);
    }

    isTouchSectionSnapEnabled() {
        return this.sections.length > 0
            && !window.matchMedia("(prefers-reduced-motion: reduce)").matches
            && window.matchMedia("(hover: none), (pointer: coarse), (max-width: 999px)").matches;
    }

    hasScrollableParentInAnyDirection(element) {
        let parent = element;

        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const canScrollVertically = /(auto|scroll|overlay)/.test(style.overflowY)
                && parent.scrollHeight > parent.clientHeight;
            const canScrollHorizontally = /(auto|scroll|overlay)/.test(style.overflowX)
                && parent.scrollWidth > parent.clientWidth;

            if (canScrollVertically || canScrollHorizontally) {
                return true;
            }

            parent = parent.parentElement;
        }

        return false;
    }

    scheduleTouchSectionSnap(delay = 150) {
        if (!this.isTouchSectionSnapEnabled() || this.isTouching || this.isNavigating || this.touchStartSectionIndex < 0) {
            return;
        }

        window.clearTimeout(this.touchSnapTimer);
        this.touchSnapTimer = window.setTimeout(() => this.snapAfterTouchScroll(), delay);
    }

    snapAfterTouchScroll() {
        if (this.isTouching || this.isNavigating) {
            return;
        }

        const targetIndex = this.getTouchSnapTargetIndex();

        this.resetTouchState();

        if (targetIndex === -1) {
            return;
        }

        this.navigateToSection(targetIndex);
    }

    getTouchSnapTargetIndex() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const startIndex = this.clamp(this.touchStartSectionIndex, 0, this.sections.length - 1);
        const startSection = this.sections[startIndex];
        const scrollDelta = scrollTop - this.touchStartScrollTop;
        const threshold = Math.min(this.touchSnapThreshold, viewportHeight * 0.08);

        if (!startSection || Math.abs(scrollDelta) < threshold) {
            return this.getNearestSectionIndex();
        }

        const direction = scrollDelta > 0 ? 1 : -1;

        if (startSection.classList.contains(this.tallSectionClass)) {
            const sectionTop = this.sectionTops.get(startSection) ?? startSection.offsetTop;
            const sectionEnd = sectionTop + Math.max(0, startSection.scrollHeight - viewportHeight);

            if (direction > 0 && scrollTop < sectionEnd - threshold) {
                return -1;
            }

            if (direction < 0 && scrollTop > sectionTop + threshold) {
                return -1;
            }
        }

        const targetIndex = startIndex + direction;

        if (targetIndex < 0 || targetIndex >= this.sections.length) {
            return -1;
        }

        return targetIndex;
    }

    getNearestSectionIndex() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        let nearestIndex = -1;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.sections.forEach((section, index) => {
            const sectionTop = this.sectionTops.get(section) ?? section.offsetTop;
            const distance = Math.abs(sectionTop - scrollTop);

            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        return nearestIndex;
    }

    resetTouchState() {
        window.clearTimeout(this.touchSnapTimer);
        this.touchSnapTimer = null;
        this.isTouching = false;
        this.touchTarget = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchStartScrollTop = 0;
        this.touchStartSectionIndex = -1;
        this.touchStartedAt = 0;
    }

    getCurrentSectionIndex() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const activeSection = this.getActiveSection(viewportHeight);

        return this.sections.indexOf(activeSection);
    }

    shouldCaptureWheelForSection(sectionIndex, direction) {
        const section = this.sections[sectionIndex];

        if (!section || !section.classList.contains(this.tallSectionClass)) {
            return true;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const sectionTop = this.sectionTops.get(section) ?? section.offsetTop;
        const sectionEnd = sectionTop + Math.max(0, section.scrollHeight - viewportHeight);

        return direction > 0
            ? scrollTop >= sectionEnd - 8
            : scrollTop <= sectionTop + 8;
    }

    scrollTallSectionWithinBounds(sectionIndex, delta, direction, shouldLockBoundary = true) {
        const section = this.sections[sectionIndex];

        if (!section || !section.classList.contains(this.tallSectionClass)) {
            return false;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const sectionTop = this.sectionTops.get(section) ?? section.offsetTop;
        const sectionEnd = sectionTop + Math.max(0, section.scrollHeight - viewportHeight);
        const absoluteDelta = Math.abs(delta);
        let targetTop = scrollTop;

        if (direction > 0) {
            if (scrollTop >= sectionEnd - 1) {
                return false;
            }

            targetTop = Math.min(sectionEnd, Math.max(sectionTop, scrollTop) + absoluteDelta);
        } else {
            if (scrollTop <= sectionTop + 1) {
                return false;
            }

            targetTop = Math.max(sectionTop, Math.min(sectionEnd, scrollTop) - absoluteDelta);
        }

        // Pixel-mode events often include trackpad momentum, so briefly hold
        // their boundary. Discrete line/page wheel events have no such tail;
        // locking those events makes Firefox swallow the next wheel notch.
        if (shouldLockBoundary && (targetTop === sectionTop || targetTop === sectionEnd)) {
            this.lockTallBoundary(section, direction);
        }

        this.resetWheelIntent();
        this.cancelSectionNavigation();
        this.suspendScrollSnap();
        this.html.style.scrollBehavior = "auto";
        window.scrollTo(0, targetTop);
        this.requestUpdate();

        return true;
    }

    lockTallBoundary(section, direction) {
        this.tallBoundaryLock = { section, direction };
        window.clearTimeout(this.tallBoundaryLockTimer);
        this.tallBoundaryLockTimer = window.setTimeout(
            () => this.clearTallBoundaryLock(),
            this.tallBoundaryLockDuration
        );
    }

    shouldHoldTallBoundary(sectionIndex, direction) {
        const section = this.sections[sectionIndex];

        return Boolean(
            section
            && this.tallBoundaryLock
            && this.tallBoundaryLock.section === section
            && this.tallBoundaryLock.direction === direction
        );
    }

    clearTallBoundaryLock() {
        this.tallBoundaryLock = null;
        window.clearTimeout(this.tallBoundaryLockTimer);
        this.tallBoundaryLockTimer = null;
    }

    resetWheelIntent() {
        this.wheelDelta = 0;
        this.wheelDirection = 0;
        window.clearTimeout(this.wheelResetTimer);
        this.wheelResetTimer = null;
    }

    navigateToSection(targetIndex, options = {}) {
        const targetSection = this.sections[targetIndex];

        if (!targetSection) {
            return;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const documentHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        const sectionTop = this.sectionTops.get(targetSection) ?? targetSection.offsetTop;
        // When moving upward into a tall section, enter at its bottom edge.
        // Jumping to its top skips the adjacent content and leaves the browser
        // in a long smooth-scroll lock, which is especially noticeable when
        // returning from the footer.
        const targetSectionEnd = sectionTop + Math.max(0, targetSection.scrollHeight - viewportHeight);
        const requestedTargetTop = options.direction < 0
        && targetSection.classList.contains(this.tallSectionClass)
            ? targetSectionEnd
            : sectionTop;
        const targetTop = Math.min(requestedTargetTop, Math.max(0, documentHeight - viewportHeight));
        const startTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const distance = Math.abs(targetTop - startTop);

        if (distance < 2) {
            return;
        }

        this.cancelSectionNavigation();
        this.clearTallBoundaryLock();
        this.restoreSuspendedScrollSnap();
        this.isNavigating = true;
        this.previousScrollBehavior = this.html.style.scrollBehavior;
        this.previousScrollSnapType = this.html.style.scrollSnapType;
        this.html.style.scrollBehavior = "auto";
        // Mandatory CSS snapping otherwise pins every animation frame to the current section,
        // which makes the movement appear as a delayed jump instead of easing.
        this.html.style.scrollSnapType = "none";

        if (options.preferNativeSmooth && this.supportsNativeSmoothScroll()) {
            this.isNativeSectionNavigation = true;
            window.scrollTo({ top: targetTop, behavior: "smooth" });
            this.watchNativeSmoothScroll(targetTop);
            return;
        }

        // A capped duration keeps each section deliberate without making the page feel unresponsive.
        const duration = this.clamp(420 + distance * 0.32, 650, 900);
        const startedAt = window.performance.now();

        const step = (now) => {
            const progress = this.clamp((now - startedAt) / duration, 0, 1);
            const easedProgress = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            window.scrollTo(0, startTop + (targetTop - startTop) * easedProgress);

            if (progress < 1) {
                this.navigationFrame = window.requestAnimationFrame(step);
                return;
            }

            this.finishSectionNavigation(targetTop);
        };

        this.navigationFrame = window.requestAnimationFrame(step);
    }

    supportsNativeSmoothScroll() {
        return (
            typeof window.CSS !== "undefined"
            && typeof window.CSS.supports === "function"
            && window.CSS.supports("scroll-behavior", "smooth")
        ) || typeof document.documentElement.style.scrollBehavior === "string";
    }

    watchNativeSmoothScroll(targetTop) {
        const startedAt = window.performance.now();
        const maxDuration = 1200;

        const step = (now) => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

            if (Math.abs(scrollTop - targetTop) < 2 || now - startedAt > maxDuration) {
                this.finishSectionNavigation(targetTop);
                return;
            }

            this.navigationFrame = window.requestAnimationFrame(step);
        };

        this.navigationFrame = window.requestAnimationFrame(step);
    }

    finishSectionNavigation(targetTop) {
        window.cancelAnimationFrame(this.navigationFrame);
        this.navigationFrame = null;
        window.scrollTo(0, targetTop);
        this.html.style.scrollBehavior = this.previousScrollBehavior;
        this.html.style.scrollSnapType = this.previousScrollSnapType;
        this.previousScrollBehavior = "";
        this.previousScrollSnapType = "";
        this.isNavigating = false;
        this.isNativeSectionNavigation = false;
        this.requestUpdate();
    }

    cancelSectionNavigation() {
        if (!this.isNavigating) {
            return;
        }

        window.cancelAnimationFrame(this.navigationFrame);
        this.navigationFrame = null;
        this.html.style.scrollBehavior = this.previousScrollBehavior;
        this.html.style.scrollSnapType = this.previousScrollSnapType;
        this.previousScrollBehavior = "";
        this.previousScrollSnapType = "";
        this.isNavigating = false;
        this.isNativeSectionNavigation = false;
    }

    suspendScrollSnap() {
        if (!this.isScrollSnapSuspended) {
            this.suspendedScrollBehavior = this.html.style.scrollBehavior;
            this.suspendedScrollSnapType = this.html.style.scrollSnapType;
            this.isScrollSnapSuspended = true;
        }

        this.html.style.scrollBehavior = "auto";
        this.html.style.scrollSnapType = "none";
    }

    restoreSuspendedScrollSnap() {
        if (!this.isScrollSnapSuspended) {
            return;
        }

        this.html.style.scrollBehavior = this.suspendedScrollBehavior;
        this.html.style.scrollSnapType = this.suspendedScrollSnapType;
        this.suspendedScrollBehavior = "";
        this.suspendedScrollSnapType = "";
        this.isScrollSnapSuspended = false;
    }

    scanSections() {
        // Footer parent blocks use the same full-screen section treatment as page content.
        // This keeps the final content-to-footer transition consistent with all other sections.
        this.contents = Array.from(document.querySelectorAll(".entry-content, footer.wp-block-template-part"));
        this.sections = [];
        this.sectionTops = new WeakMap();

        this.contents.forEach((content) => {
            const sections = Array.from(content.children).filter((child) => this.isSectionCandidate(child));

            content.classList.toggle(this.contentClass, sections.length > 0);

            Array.from(content.children).forEach((child) => {
                if (!sections.includes(child)) {
                    child.classList.remove(
                        this.sectionClass,
                        this.activeClass,
                        this.tallSectionClass,
                        this.constrainInnerClass,
                        this.defaultBackgroundClass
                    );
                    child.removeAttribute("data-section-scroll-index");
                    child.style.removeProperty("--image-theme-scroll-section-progress");
                    child.style.removeProperty("--image-theme-scroll-section-z-index");
                    child.style.removeProperty("--image-theme-scroll-section-background");
                    child.style.removeProperty("--image-theme-scroll-section-content-width");
                    child.style.removeProperty("--image-theme-scroll-section-height");
                    child.style.removeProperty("--image-theme-scroll-section-sticky-top");
                }
            });

            sections.forEach((section) => {
                const sectionIndex = this.sections.length + 1;
                const sectionContentWidth = this.getOriginalSectionContentWidth(section);
                const sectionBackgroundColor = this.getOriginalSectionBackgroundColor(section);
                const hasOwnBackground = sectionBackgroundColor !== this.defaultBackgroundColor;

                section.classList.add(this.sectionClass);
                section.dataset.sectionScrollIndex = String(sectionIndex);
                section.style.setProperty("--image-theme-scroll-section-z-index", String(sectionIndex));
                section.style.setProperty("--image-theme-scroll-section-background", sectionBackgroundColor);
                section.style.setProperty("--image-theme-scroll-section-content-width", `${sectionContentWidth}px`);
                section.classList.toggle(this.constrainInnerClass, this.shouldConstrainSectionInnerWidth(sectionContentWidth));
                section.classList.toggle(this.defaultBackgroundClass, !hasOwnBackground);
                this.sections.push(section);
            });
        });

        this.body.classList.toggle(this.readyClass, this.sections.length > 0);
        this.html.classList.toggle(this.readyClass, this.sections.length > 0);
        this.updateSectionMetrics();
        this.updateSectionProgress(false);
    }

    isSectionCandidate(element) {
        if (!(element instanceof HTMLElement)) {
            return false;
        }

        if (element.matches("script, style, link, template, footer, .wp-block-template-part")) {
            return false;
        }

        if (element.matches("[data-section-scroll-exclude], .image-theme-scroll-effect__exclude")) {
            return false;
        }

        if (element.matches("p") && element.textContent.trim() === "" && !element.querySelector("img, video, iframe, svg")) {
            return false;
        }

        if (element.classList.contains("wp-block-spacer") || element.classList.contains("wp-block-separator")) {
            return false;
        }

        return Array.from(element.classList).some((className) => className.indexOf("wp-block-") === 0)
            || element.classList.contains("glide")
            || element.getBoundingClientRect().height > 1
            || element.textContent.trim() !== "";
    }

    hasOwnBackground(element) {
        if (element.classList.contains("has-background") || element.className.indexOf("has-") !== -1 && element.className.indexOf("-background") !== -1) {
            return true;
        }

        const style = window.getComputedStyle(element);
        const backgroundColor = style.backgroundColor;
        const backgroundImage = style.backgroundImage;

        if (backgroundImage && backgroundImage !== "none") {
            return true;
        }

        return this.isVisibleColor(backgroundColor);
    }

    getOriginalSectionBackgroundColor(element) {
        return this.withOriginalSectionStyles(element, () => this.getSectionBackgroundColor(element));
    }

    getOriginalSectionContentWidth(element) {
        return this.withOriginalSectionStyles(element, () => {
            const rect = element.getBoundingClientRect();

            return Math.round(rect.width || window.innerWidth);
        });
    }

    updateSectionTops() {
        // Measure only after every section has its final padding and tall height.
        // Sticky offsets depend on the current scroll position, so temporarily
        // unstick all sections without removing any of their layout styles.
        const positions = this.sections.map((section) => ({
            section,
            position: section.style.getPropertyValue("position"),
            priority: section.style.getPropertyPriority("position"),
            top: section.style.getPropertyValue("top"),
            topPriority: section.style.getPropertyPriority("top"),
        }));

        positions.forEach(({ section }) => {
            section.style.setProperty("position", "relative", "important");
            section.style.setProperty("top", "0px", "important");
        });

        try {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
            this.sections.forEach((section) => {
                // Retain subpixel precision; rounding each section independently
                // can put the wheel boundary on the wrong side of a snap point.
                this.sectionTops.set(section, section.getBoundingClientRect().top + scrollTop);
            });
        } finally {
            positions.forEach(({ section, position, priority, top, topPriority }) => {
                if (position) {
                    section.style.setProperty("position", position, priority);
                } else {
                    section.style.removeProperty("position");
                }
                if (top) {
                    section.style.setProperty("top", top, topPriority);
                } else {
                    section.style.removeProperty("top");
                }
            });
        }
    }

    shouldConstrainSectionInnerWidth(sectionContentWidth) {
        return window.innerWidth - sectionContentWidth > 24;
    }

    withOriginalSectionStyles(element, callback) {
        const restoreClasses = [
            this.sectionClass,
            this.activeClass,
            this.tallSectionClass,
            this.constrainInnerClass,
            this.defaultBackgroundClass,
        ].filter((className) => element.classList.contains(className));
        const restoredProperties = [
            "--image-theme-scroll-section-progress",
            "--image-theme-scroll-section-z-index",
            "--image-theme-scroll-section-background",
            "--image-theme-scroll-section-content-width",
            "--image-theme-scroll-section-height",
            "--image-theme-scroll-section-sticky-top",
        ].map((property) => [property, element.style.getPropertyValue(property)]);

        restoreClasses.forEach((className) => element.classList.remove(className));
        restoredProperties.forEach(([property]) => element.style.removeProperty(property));

        const result = callback();

        restoreClasses.forEach((className) => element.classList.add(className));
        restoredProperties.forEach(([property, value]) => {
            if (value) {
                element.style.setProperty(property, value);
            }
        });

        return result;
    }

    withSectionMeasurementStyles(element, callback) {
        const hadTallClass = element.classList.contains(this.tallSectionClass);
        const restoredProperties = [
            "--image-theme-scroll-section-height",
            "--image-theme-scroll-section-sticky-top",
        ].map((property) => [property, element.style.getPropertyValue(property)]);

        element.classList.remove(this.tallSectionClass);
        restoredProperties.forEach(([property]) => element.style.removeProperty(property));

        const result = callback();

        if (hadTallClass) {
            element.classList.add(this.tallSectionClass);
        }

        restoredProperties.forEach(([property, value]) => {
            if (value) {
                element.style.setProperty(property, value);
            }
        });

        return result;
    }

    getSectionBackgroundColor(element) {
        const presetBackgroundColor = this.getPresetBackgroundColor(element);

        if (presetBackgroundColor) {
            return presetBackgroundColor;
        }

        const style = window.getComputedStyle(element);

        if (this.isVisibleColor(style.backgroundColor)) {
            return style.backgroundColor;
        }

        return this.defaultBackgroundColor;
    }

    getPresetBackgroundColor(element) {
        const backgroundClass = Array.from(element.classList).find((className) => (
            className.indexOf("has-") === 0
            && className.indexOf("-background-color") > -1
        ));

        if (!backgroundClass) {
            return null;
        }

        const slug = backgroundClass
            .replace(/^has-/, "")
            .replace(/-background-color$/, "");
        const presetValue = window
            .getComputedStyle(document.documentElement)
            .getPropertyValue(`--wp--preset--color--${slug}`)
            .trim();

        return presetValue || null;
    }

    isVisibleColor(color) {
        if (!color || color === "transparent") {
            return false;
        }

        const rgbaMatch = color.match(/rgba?\(([^)]+)\)/);

        if (!rgbaMatch) {
            return true;
        }

        const values = rgbaMatch[1].split(",").map((value) => Number.parseFloat(value.trim()));

        return values.length < 4 || values[3] > 0;
    }

    observeContentChanges() {
        if (!("MutationObserver" in window)) {
            return;
        }

        this.contents.forEach((content) => {
            const observer = new MutationObserver((mutations) => {
                const hasDirectChildChange = mutations.some((mutation) => mutation.target === content);

                if (!hasDirectChildChange) {
                    return;
                }

                this.scanSections();
                this.requestUpdate();
            });

            observer.observe(content, { childList: true });
        });
    }

    requestUpdate() {
        if (this.ticking) {
            return;
        }

        this.ticking = true;

        window.requestAnimationFrame(() => {
            this.ticking = false;

            if (this.isNativeSectionNavigation) {
                return;
            }

            this.updateSectionProgress();
        });
    }

    updateSectionProgress(shouldUpdateMetrics = false) {
        if (shouldUpdateMetrics) {
            this.updateSectionMetrics();
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const activeSection = this.getActiveSection(viewportHeight);

        this.sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const sectionCenter = rect.top + rect.height / 2;
            const viewportCenter = viewportHeight / 2;
            const progress = this.clamp((viewportCenter - sectionCenter) / viewportHeight, -1, 1);

            section.style.setProperty("--image-theme-scroll-section-progress", progress.toFixed(4));
            section.classList.toggle(this.activeClass, section === activeSection);
        });

        if (!this.isNavigating) {
            const activeIndex = this.sections.indexOf(activeSection);

            if (this.shouldKeepScrollSnapSuspended(activeIndex)) {
                this.suspendScrollSnap();
            } else {
                this.restoreSuspendedScrollSnap();
            }
        }
    }

    getActiveSection(viewportHeight) {
        if (!this.sections.length) {
            return null;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const activationPoint = scrollTop + viewportHeight * 0.5;
        let activeSection = this.sections[0];

        this.sections.forEach((section) => {
            const sectionTop = this.sectionTops.get(section);

            if ((sectionTop ?? section.offsetTop) <= activationPoint) {
                activeSection = section;
            }
        });

        return activeSection;
    }

    shouldKeepScrollSnapSuspended(sectionIndex) {
        const section = this.sections[sectionIndex];

        if (!section || !section.classList.contains(this.tallSectionClass)) {
            return false;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const sectionTop = this.sectionTops.get(section) ?? section.offsetTop;
        const sectionEnd = sectionTop + Math.max(0, section.scrollHeight - viewportHeight);

        return scrollTop > sectionTop + 1 && scrollTop <= sectionEnd + 1;
    }

    updateSectionMetrics() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const tallThreshold = viewportHeight + 32;

        this.sections.forEach((section) => {
            // Measure the scroll-effect layout, but without the tall state from the
            // previous pass. The section styles add the vertical card padding, so removing
            // every scroll-effect class would under-measure tall content and skip its
            // bottom padding before moving to the next section.
            const sectionHeight = this.getOriginalSectionHeight(section);
            const isTall = sectionHeight > tallThreshold;

            section.classList.toggle(this.tallSectionClass, isTall);

            if (isTall) {
                section.style.setProperty("--image-theme-scroll-section-height", `${sectionHeight}px`);
                section.style.setProperty("--image-theme-scroll-section-sticky-top", `${viewportHeight - sectionHeight}px`);
            } else {
                section.style.removeProperty("--image-theme-scroll-section-height");
                section.style.removeProperty("--image-theme-scroll-section-sticky-top");
            }
        });

        this.updateSectionTops();
    }

    getOriginalSectionHeight(element) {
        return this.withSectionMeasurementStyles(element, () => Math.ceil(Math.max(
            element.scrollHeight,
            element.getBoundingClientRect().height
        )));
    }

    clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
    }
}

export default SectionScrollEffect;